import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useFetchModelsQuery, useCreateModelMutation } from '../../../services/modelsApi'
import NoModelsCard from '../ModelsOverview/components/NoModelsCard'
import NewModelModal from '../../modals/NewModelModal'
import ErrorDialog from '../../modals/ErrorDialog'
import CurlEndpointCard from '../../tools/CurlEndpointCard'
import { selectSelectedModelId, setSelectedModelId } from '../../../store/slices/modelSlice'
import { useNavigate } from 'react-router-dom'
import { Tabs, Tab } from 'react-bootstrap'
import DeploymentOverview from './components/DeploymentOverview'

const ViewContainer = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
`

const ContentSection = styled.div`
  flex: 1;
  min-height: 0;
  padding: 1.25rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const StyledTabs = styled(Tabs)`
  border-bottom: none;
  margin-bottom: 1.5rem;

  .nav-item {
    margin-right: 0.5rem;
  }

  .nav-link {
    background: #2a2a2a;
    border: none;
    color: #a0a0a0;
    padding: 0.75rem 1.25rem;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      background: #3a3a3a;
      color: #fff;
    }

    &.active {
      background: #4a4a4a;
      color: #fff;
      font-weight: 500;
    }
  }
`

const ModelDeploymentView: React.FC = () => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const [showNewModelModal, setShowNewModelModal] = useState(false)
  const [selectedBaseModel, setSelectedBaseModel] = useState<{
    id: string
    name: string
  } | null>(null)
  const [filterText, setFilterText] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<string>('overview')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId }
  )
  const [createModel] = useCreateModelMutation()

  const handleCreateModel = () => {
    setShowNewModelModal(true)
  }

  const generateRandomName = () => {
    const adjectives = ['Agile', 'Smart', 'Quick', 'Bright', 'Sharp']
    const nouns = ['Vision', 'Eye', 'Sight', 'View', 'Lens']
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)]
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)]
    return `${randomAdjective} ${randomNoun}`
  }

  const handleGenerateNewModel = async () => {
    if (selectedBaseModel && selectedProjectId) {
      try {
        const newModelName = generateRandomName()
        const newModel = await createModel({
          project_id: selectedProjectId,
          name: newModelName,
          description: '',
        }).unwrap()

        // Wait for a brief moment to allow the cache to update
        await new Promise((resolve) => setTimeout(resolve, 100))

        // Set the selected model ID
        dispatch(
          setSelectedModelId({
            modelId: newModel.id,
            projectId: selectedProjectId || '',
          })
        )

        // Navigate to the training subview
        navigate(`/models/${selectedProjectId}/training`)

        // Close the modal
        setShowNewModelModal(false)
        setSelectedBaseModel(null)
        setFilterText('')
      } catch (err: any) {
        const message = err?.data?.message || 'Failed to create new model'
        setErrorMessage(message)
      }
    }
  }

  const selectedModel = modelsData?.models?.find((m) => m.id === selectedModelId)

  useEffect(() => {
    if (selectedModel?.state === 'TRAINING' || selectedModel?.state === 'INITIALIZED') {
      navigate(`/models/${selectedProjectId}/training`)
    }
  }, [selectedModel?.state, navigate, selectedProjectId])

  return (
    <ViewContainer>
      {!modelsData?.models || modelsData.models.length === 0 ? (
        <>
          <NoModelsCard onCreateModel={handleCreateModel} />
          <NewModelModal
            show={showNewModelModal}
            onClose={() => {
              setShowNewModelModal(false)
              setSelectedBaseModel(null)
              setFilterText('')
            }}
            baseModels={[
              {
                id: 'baseModel1',
                name: 'AG Base',
                image: '/agileview-eye.png',
              },
            ]}
            filterText={filterText}
            setFilterText={setFilterText}
            selectedBaseModel={selectedBaseModel}
            onBaseModelSelect={setSelectedBaseModel}
            onGenerateNewModel={handleGenerateNewModel}
          />
          <ErrorDialog
            show={!!errorMessage}
            onHide={() => setErrorMessage(null)}
            message={errorMessage || ''}
          />
        </>
      ) : (
        <ContentSection>
          <StyledTabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || 'overview')}
            id="deployment-tabs"
          >
            <Tab eventKey="overview" title="Deployment Overview">
              <DeploymentOverview model={selectedModel} />
            </Tab>
            <Tab eventKey="endpoint" title="Endpoint Information">
              <CurlEndpointCard deployment={selectedModel?.deployment} />
            </Tab>
          </StyledTabs>
        </ContentSection>
      )}
    </ViewContainer>
  )
}

export default ModelDeploymentView
